import type { DiGraph } from "./digraph.js";
import { betweennessUndirected } from "./betweenness.js";
import { hits } from "./hits.js";
import { pageRank } from "./pagerank.js";
import { topologicalOrder } from "./topological.js";
import { buildCurriculumGraph } from "./curriculum.js";

export type GraphScores = {
  pageRank: Map<string, number>;
  hub: Map<string, number>;
  authority: Map<string, number>;
  betweenness: Map<string, number>;
  /** 0 = earliest in a teaching order, 1 = latest (if DAG); else 0.5. */
  topoRank: Map<string, number>;
};

/**
 * All centralities in one pass for a curriculum (or any) `DiGraph`.
 */
export function precomputeGraphScores(g: DiGraph): GraphScores {
  const pr = pageRank(g);
  const { hub, authority } = hits(g);
  const bet = betweennessUndirected(g);
  const topo = topologicalOrder(g);
  const topoRank = new Map<string, number>();
  if (topo.ok) {
    const order = topo.order;
    const n = order.length;
    order.forEach((v, i) => {
      topoRank.set(v, n > 1 ? i / (n - 1) : 0.5);
    });
  } else {
    for (const v of g.nodes) {
      topoRank.set(v, 0.5);
    }
  }
  return { pageRank: pr, hub, authority, betweenness: bet, topoRank };
}

export function precomputeCurriculumScores(): GraphScores {
  return precomputeGraphScores(buildCurriculumGraph());
}

/** Min–max to [0,1]; all equal → 0.5. */
export function normalize01(m: ReadonlyMap<string, number>, keys: string[]): Map<string, number> {
  const out = new Map<string, number>();
  if (keys.length === 0) return out;
  let mn = Number.POSITIVE_INFINITY;
  let mx = Number.NEGATIVE_INFINITY;
  for (const k of keys) {
    const v = m.get(k) ?? 0;
    if (v < mn) mn = v;
    if (v > mx) mx = v;
  }
  if (mn >= mx) {
    for (const k of keys) {
      out.set(k, 0.5);
    }
    return out;
  }
  for (const k of keys) {
    out.set(k, ((m.get(k) ?? 0) - mn) / (mx - mn));
  }
  return out;
}
